import React, { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TicketFilters from './components/TicketFilters';
import TicketSection from './components/TicketSection';
import TransferModal from './components/TransferModal';

const MyTicketsPage = () => {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date-asc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Mock user authentication check
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Simulate authentication check
    const checkAuth = () => {
      const authStatus = localStorage.getItem('isAuthenticated') === 'true';
      setIsAuthenticated(authStatus);
      setIsLoading(false);
      
      if (!authStatus) {
        router?.push('/login');
      }
    };

    checkAuth();
  }, [router]);

  // Mock tickets data
  const mockTickets = [
    {
      id: 'TIX-001',
      eventId: 'EVT-001',
      eventName: 'Summer Music Festival 2024',
      eventImage: 'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=800&h=400&fit=crop',
      eventDate: '2024-07-15',
      eventTime: '18:00',
      venue: 'Central Park Amphitheater',
      ticketType: 'General Admission',
      holderName: 'John Doe',
      price: 89.99,
      orderId: 'ORD-12345',
      purchaseDate: '2024-06-01',
      status: 'active',
      qrCode: 'QR123456789',
      transferable: true,
      eventType: 'festival',
      upgrades: [
        { name: 'VIP Parking', price: 25.00 },
        { name: 'Fast Track Entry', price: 15.00 }
      ],
      merchandise: [
        { name: 'Festival T-Shirt', quantity: 1, totalPrice: 29.99 }
      ],
      paymentMethod: 'Visa ending in 4242',
      totalPaid: 159.98
    },
    {
      id: 'TIX-002',
      eventId: 'EVT-002',
      eventName: 'Broadway Musical: Hamilton',
      eventImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=400&fit=crop',
      eventDate: '2024-08-22',
      eventTime: '20:00',
      venue: 'Majestic Theatre',
      ticketType: 'Orchestra Seat',
      holderName: 'Jane Smith',
      price: 199.99,
      orderId: 'ORD-12346',
      purchaseDate: '2024-05-15',
      status: 'active',
      qrCode: 'QR987654321',
      transferable: false,
      eventType: 'theater',
      upgrades: [],
      merchandise: [
        { name: 'Program Book', quantity: 1, totalPrice: 15.00 }
      ],
      paymentMethod: 'Mastercard ending in 8888',
      totalPaid: 214.99
    },
    {
      id: 'TIX-003',
      eventId: 'EVT-003',
      eventName: 'NBA Finals Game 7',
      eventImage: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800&h=400&fit=crop',
      eventDate: '2024-06-20',
      eventTime: '21:00',
      venue: 'Madison Square Garden',
      ticketType: 'Lower Bowl',
      holderName: 'Mike Johnson',
      price: 450.00,
      orderId: 'ORD-12347',
      purchaseDate: '2024-06-18',
      status: 'used',
      qrCode: 'QR456789123',
      transferable: false,
      eventType: 'sports',
      upgrades: [
        { name: 'Premium Concessions', price: 50.00 }
      ],
      merchandise: [
        { name: 'Team Jersey', quantity: 1, totalPrice: 89.99 },
        { name: 'Championship Hat', quantity: 1, totalPrice: 34.99 }
      ],
      paymentMethod: 'American Express ending in 1234',
      totalPaid: 624.98
    },
    {
      id: 'TIX-004',
      eventId: 'EVT-004',
      eventName: 'Comedy Night with Dave Chappelle',
      eventImage: 'https://images.unsplash.com/photo-1585699254908-6b0b6c8b9b6b?w=800&h=400&fit=crop',
      eventDate: '2024-05-10',
      eventTime: '20:30',
      venue: 'Comedy Club Downtown',
      ticketType: 'Premium Seating',
      holderName: 'Sarah Wilson',
      price: 75.00,
      orderId: 'ORD-12348',
      purchaseDate: '2024-04-20',
      status: 'expired',
      qrCode: 'QR789123456',
      transferable: true,
      eventType: 'comedy',
      upgrades: [],
      merchandise: [],
      paymentMethod: 'Visa ending in 5678',
      totalPaid: 75.00
    },
    {
      id: 'TIX-005',
      eventId: 'EVT-005',
      eventName: 'Tech Conference 2024',
      eventImage: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=400&fit=crop',
      eventDate: '2024-09-15',
      eventTime: '09:00',
      venue: 'Convention Center',
      ticketType: 'All Access Pass',
      holderName: 'Alex Chen',
      price: 299.99,
      orderId: 'ORD-12349',
      purchaseDate: '2024-07-01',
      status: 'active',
      qrCode: 'QR321654987',
      transferable: true,
      eventType: 'conference',
      upgrades: [
        { name: 'Workshop Access', price: 99.99 },
        { name: 'Networking Lunch', price: 49.99 }
      ],
      merchandise: [
        { name: 'Conference Swag Bag', quantity: 1, totalPrice: 25.00 }
      ],
      paymentMethod: 'PayPal',
      totalPaid: 474.97
    }
  ];

  // Filter and sort tickets
  const filteredAndSortedTickets = useMemo(() => {
    let filtered = mockTickets;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(ticket =>
        ticket?.eventName?.toLowerCase()?.includes(query) ||
        ticket?.venue?.toLowerCase()?.includes(query) ||
        ticket?.orderId?.toLowerCase()?.includes(query) ||
        ticket?.holderName?.toLowerCase()?.includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered?.filter(ticket => ticket?.status === statusFilter);
    }

    // Apply event type filter
    if (eventTypeFilter !== 'all') {
      filtered = filtered?.filter(ticket => ticket?.eventType === eventTypeFilter);
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'date-asc':
          return new Date(a.eventDate) - new Date(b.eventDate);
        case 'date-desc':
          return new Date(b.eventDate) - new Date(a.eventDate);
        case 'purchase-desc':
          return new Date(b.purchaseDate) - new Date(a.purchaseDate);
        case 'purchase-asc':
          return new Date(a.purchaseDate) - new Date(b.purchaseDate);
        case 'name-asc':
          return a?.eventName?.localeCompare(b?.eventName);
        case 'name-desc':
          return b?.eventName?.localeCompare(a?.eventName);
        default:
          return 0;
      }
    });

    return filtered;
  }, [mockTickets, searchQuery, statusFilter, eventTypeFilter, sortBy]);

  // Separate tickets by status
  const upcomingTickets = filteredAndSortedTickets?.filter(ticket => 
    ticket?.status === 'active' && new Date(ticket.eventDate) >= new Date()
  );
  
  const pastTickets = filteredAndSortedTickets?.filter(ticket => 
    ticket?.status === 'used' || ticket?.status === 'expired' || 
    (ticket?.status === 'active' && new Date(ticket.eventDate) < new Date())
  );

  const handleClearFilters = () => {
    setSearchQuery('');
    setSortBy('date-asc');
    setStatusFilter('all');
    setEventTypeFilter('all');
  };

  const handleTransfer = (ticketId) => {
    const ticket = mockTickets?.find(t => t?.id === ticketId);
    setSelectedTicket(ticket);
    setTransferModalOpen(true);
  };

  const handleTransferSubmit = async (transferData) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log('Transfer submitted:', transferData);
    // In real app, update ticket status and refresh data
  };

  const handleResendEmail = async (ticketId) => {
    // Simulate API call
    console.log('Resending email for ticket:', ticketId);
    // Show success message
  };

  const handleAddToWallet = async (ticketId, walletType) => {
    // Simulate wallet integration
    console.log('Adding to wallet:', ticketId, walletType);
    // In real app, generate wallet pass
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your tickets...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect to login
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">My Tickets</h1>
              <p className="text-muted-foreground mt-1">
                Manage your event tickets and access QR codes
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => router?.push('/home')}
              iconName="Plus"
            >
              Find Events
            </Button>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <TicketFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          onSortChange={setSortBy}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          eventTypeFilter={eventTypeFilter}
          onEventTypeFilterChange={setEventTypeFilter}
          onClearFilters={handleClearFilters}
        />

        {/* No Results */}
        {filteredAndSortedTickets?.length === 0 && (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Search" size={40} className="text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">No tickets found</h3>
            <p className="text-muted-foreground mb-6">
              Try adjusting your search criteria or filters to find your tickets.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="outline" onClick={handleClearFilters}>
                Clear All Filters
              </Button>
              <Button variant="default" onClick={() => router?.push('/home')}>
                Browse Events
              </Button>
            </div>
          </div>
        )}

        {/* Tickets Sections */}
        {filteredAndSortedTickets?.length > 0 && (
          <>
            {/* Upcoming Events */}
            <TicketSection
              title="Upcoming Events"
              tickets={upcomingTickets}
              emptyMessage="No upcoming events found. Browse events to find your next experience!"
              emptyIcon="Calendar"
              onTransfer={handleTransfer}
              onResendEmail={handleResendEmail}
              onAddToWallet={handleAddToWallet}
            />

            {/* Past Events */}
            <TicketSection
              title="Past Events"
              tickets={pastTickets}
              emptyMessage="No past events found."
              emptyIcon="History"
              onTransfer={handleTransfer}
              onResendEmail={handleResendEmail}
              onAddToWallet={handleAddToWallet}
            />
          </>
        )}

        {/* Empty State for New Users */}
        {mockTickets?.length === 0 && (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Ticket" size={40} className="text-primary" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">No tickets yet</h3>
            <p className="text-muted-foreground mb-6">
              Start exploring events and purchase your first tickets to see them here.
            </p>
            <Button
              variant="default"
              size="lg"
              onClick={() => router?.push('/home')}
              iconName="Search"
            >
              Discover Events
            </Button>
          </div>
        )}
      </div>
      {/* Transfer Modal */}
      <TransferModal
        isOpen={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        ticket={selectedTicket}
        onTransfer={handleTransferSubmit}
      />
    </div>
  );
};

export default MyTicketsPage;